import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.card, padding: '24px' },
  input: { ...s.input, padding: '10px' },
  label: { ...s.labelNormal },
  select: { ...s.select, padding: '9px', width: 'auto' },
  th: { ...s.th, padding: '8px 10px' },
  td: { ...s.td, padding: '7px 10px' },
}

const DEFAULT_CONFIG = {
  is_active: false,
  auto_schedule: false,
  w_sales: 25, w_conversion: 20, w_views: 10, w_cart: 10, w_rating: 10,
  w_recency: 10, w_discount: 8, w_margin: 5, w_sameday: 2, w_price: 0,
  stock_rule: true, min_views: 0, discount_dir: 'high', price_dir: 'none',
  last_run_at: null, last_run_count: null, last_run_outstock: null, last_run_fail: null,
}

const WEIGHTS = [
  { key: 'w_sales', label: 'Satış', hint: 'Toplam satış adedi' },
  { key: 'w_conversion', label: 'Dönüşüm oranı', hint: 'Satış ÷ görüntüleme (verimli satan)' },
  { key: 'w_views', label: 'Görüntüleme', hint: 'Görüntülenme sayısı' },
  { key: 'w_cart', label: 'Sepete eklenme', hint: 'Sepete ekleme (Smart Event verisi)' },
  { key: 'w_rating', label: 'Puan + yorum', hint: 'Yorum puanı, yorum sayısıyla ağırlıklı' },
  { key: 'w_recency', label: 'Yenilik', hint: 'Ürün oluşturma tarihi (yeni = yüksek)' },
  { key: 'w_discount', label: 'İndirim oranı', hint: 'İndirim yüzdesi' },
  { key: 'w_margin', label: 'Kâr marjı', hint: '(Satış − Alış) ÷ Satış' },
  { key: 'w_sameday', label: 'Aynı gün kargo', hint: 'Aynı gün kargo etiketi' },
  { key: 'w_price', label: 'Fiyat', hint: 'Fiyat yönü (Kurallar sekmesinden seç)' },
]

export default function SmartRanking() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [tab, setTab] = useState('weights')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState('')
  const [preview, setPreview] = useState(null)
  const [msg, setMsg] = useState(null)
  const [page, setPage] = useState(0)
  const pollRef = useRef(null)

  useEffect(() => { setPage(0) }, [preview])

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  function stopPoll() { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }

  function startPoll() {
    stopPoll()
    const started = Date.now()
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > 12 * 60 * 1000) { stopPoll(); setBusy(''); return }
      try {
        const r = await axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-ranking-config`, { headers })
        const c = r.data || {}
        if (c.job_status === 'done') {
          stopPoll(); setBusy('')
          setConfig(prev => ({ ...prev, ...c }))
          if (c.last_preview) setPreview(c.last_preview)
          setMsg({ type: 'ok', text: c.job_message || 'Tamamlandı.' })
        } else if (c.job_status === 'error') {
          stopPoll(); setBusy('')
          setMsg({ type: 'err', text: c.job_message || 'İşlem sırasında hata oluştu.' })
        }
      } catch (e) { /* geçici — poll'a devam */ }
    }, 3000)
  }

  useEffect(() => {
    stopPoll(); setPreview(null); setMsg(null); setBusy('')
    if (!selectedBrand) return
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-ranking-config`, { headers })
      .then(res => {
        const c = res.data
        if (c && c.brand_id) {
          setConfig({ ...DEFAULT_CONFIG, ...c })
          if (c.last_preview) setPreview(c.last_preview)
          if (c.job_status === 'running') { setBusy(c.job_type || 'apply'); startPoll() }
        }
      })
      .catch(() => {})
    return stopPoll
  }, [selectedBrand?.id]) // eslint-disable-line

  function set(key, value) { setConfig(c => ({ ...c, [key]: value })) }

  function configPayload() {
    return {
      is_active: config.is_active,
      auto_schedule: config.auto_schedule,
      w_sales: config.w_sales, w_conversion: config.w_conversion, w_views: config.w_views,
      w_cart: config.w_cart, w_rating: config.w_rating, w_recency: config.w_recency,
      w_discount: config.w_discount, w_margin: config.w_margin, w_sameday: config.w_sameday, w_price: config.w_price,
      stock_rule: config.stock_rule, min_views: config.min_views,
      discount_dir: config.discount_dir, price_dir: config.price_dir,
    }
  }

  async function handleSave() {
    setSaving(true)
    await axios.put(`${API}/admin/brands/${selectedBrand.id}/smart-ranking-config`, configPayload(), { headers })
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  async function run(action) {
    setBusy(action); setMsg(null)
    if (action === 'preview') setPreview(null)
    try {
      await axios.put(`${API}/admin/brands/${selectedBrand.id}/smart-ranking-config`, configPayload(), { headers })
      const res = await axios.post(`${API}/admin/brands/${selectedBrand.id}/smart-ranking/${action}`, {}, { headers })
      if (res.data && res.data.busy) {
        setMsg({ type: 'err', text: 'Zaten çalışan bir işlem var — bitince sonuç görünecek.' })
      }
      startPoll()
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || e.message })
      setBusy('')
    }
  }

  function downloadCsv() {
    const rows = (preview && preview.top) || []
    if (!rows.length) return
    const header = ['Sıra', 'Ürün Kodu', 'Ürün Adı', 'Yayınevi', 'Yazar', 'Skor', 'Satış', 'Görüntüleme', 'Stok', 'İndirim%']
    const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"'
    const lines = [header.join(';')]
    rows.forEach((p, i) => {
      lines.push([i + 1, p.code, p.name, p.publisher, p.author, p.score, p.sales, p.views, p.stock, p.disc].map(esc).join(';'))
    })
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `smart-ranking-${(selectedBrand.name || 'marka').replace(/\s+/g, '-')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  const totalW = WEIGHTS.reduce((s, w) => s + (parseInt(config[w.key]) || 0), 0)

  const previewRows = (preview && preview.top) || []
  const PER = 20
  const totalPages = Math.max(1, Math.ceil(previewRows.length / PER))
  const curPage = Math.min(page, totalPages - 1)
  const pageRows = previewRows.slice(curPage * PER, curPage * PER + PER)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <div>
          <h2 style={s.pageTitle}>Smart Ranking — {selectedBrand.name}</h2>
          <p style={s.pageSubtitle}>Kategori sayfalarında ürünleri kalite skoruna göre sırala (Sıra No / ListNo)</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={ui.status(config.is_active)}>
            <span style={ui.statusDot(config.is_active)} />
            {config.is_active ? 'Aktif' : 'Pasif'}
          </span>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, padding: '10px 20px' }}>
            {saving ? 'Kaydediliyor...' : saved ? '✓ Kaydedildi' : 'Kaydet'}
          </button>
        </div>
      </div>

      <div style={ui.tabBar}>
        {[
          { key: 'weights', label: 'Ağırlıklar' },
          { key: 'rules', label: 'Kurallar' },
          { key: 'run', label: 'Çalıştır' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'weights' && (
        <div style={style.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            <label style={{ ...style.label, margin: 0 }}>Kriter ağırlıkları <span style={{ fontSize: '12px', color: colors.textMuted, fontWeight: 400 }}>(0-100; oransal — mutlak toplam olmak zorunda değil)</span></label>
            <span style={{ ...ui.numMuted, fontSize: '12px' }}>Toplam: {totalW}</span>
          </div>
          {WEIGHTS.map(w => (
            <div key={w.key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 52px', gap: '12px', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${colors.borderLight}` }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>{w.label}</div>
                <div style={{ fontSize: '11px', color: colors.textMuted }}>{w.hint}</div>
              </div>
              <input type="range" min="0" max="100" value={config[w.key] || 0}
                onChange={e => set(w.key, parseInt(e.target.value))}
                style={{ width: '100%', accentColor: colors.primary }} />
              <input type="number" min="0" max="100" value={config[w.key] || 0}
                onChange={e => set(w.key, Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                style={{ ...style.input, ...ui.num, padding: '6px', textAlign: 'center' }} />
            </div>
          ))}
        </div>
      )}

      {tab === 'rules' && (
        <div style={style.card}>
          <div style={{ marginBottom: '20px' }}>
            <label style={style.label}>Durum</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', marginBottom: '8px' }}>
              <input type="checkbox" checked={config.is_active} onChange={e => set('is_active', e.target.checked)} />
              Smart Ranking aktif
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.auto_schedule} onChange={e => set('auto_schedule', e.target.checked)} />
              Her gece 00:00'da otomatik uygula <span style={{ fontSize: '12px', color: colors.textMuted }}>(günde 1 kez)</span>
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.stock_rule !== false} onChange={e => set('stock_rule', e.target.checked)} />
              Stok kuralı: stokta olmayan ürünleri en dibe indir
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={style.label}>Min. görüntüleme eşiği</label>
              <input type="number" min="0" style={style.input} value={config.min_views || 0}
                onChange={e => set('min_views', Math.max(0, parseInt(e.target.value) || 0))} />
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Eşik altı ürün öne çıkmaz (0 = kapalı)</p>
            </div>
            <div>
              <label style={style.label}>İndirim yönü</label>
              <select style={{ ...style.select, width: '100%' }} value={config.discount_dir || 'high'} onChange={e => set('discount_dir', e.target.value)}>
                <option value="high">Yüksek indirim önce</option>
                <option value="low">Düşük indirim önce</option>
              </select>
            </div>
            <div>
              <label style={style.label}>Fiyat yönü</label>
              <select style={{ ...style.select, width: '100%' }} value={config.price_dir || 'none'} onChange={e => set('price_dir', e.target.value)}>
                <option value="none">Etkisiz</option>
                <option value="low">Ucuz önce</option>
                <option value="high">Pahalı önce</option>
              </select>
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Fiyat ağırlığı &gt; 0 ise etkir</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'run' && (
        <>
          <div style={style.card}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => run('preview')} disabled={!!busy} style={{ ...s.btnSecondary, padding: '10px 18px' }}>
                {busy === 'preview' ? 'Hesaplanıyor...' : 'Önizle (dry-run)'}
              </button>
              <button onClick={() => run('apply')} disabled={!!busy} style={{ ...s.btnPrimary, padding: '10px 18px' }}>
                {busy === 'apply' ? 'Uygulanıyor...' : 'Şimdi Uygula'}
              </button>
              <button onClick={() => run('revert')} disabled={!!busy} style={{ ...ui.btnDangerQuiet, fontSize: '13px', padding: '10px 18px' }}>
                {busy === 'revert' ? 'Geri alınıyor...' : 'Geri Al (son yedek)'}
              </button>
              <span className="sx-full-sm" style={{ fontSize: '12px', color: colors.textSecondary, marginLeft: 'auto' }}>
                Önizle yazmaz. Uygula ListNo yazar (öncesinde otomatik yedek alır).
              </span>
            </div>
            {busy && (
              <div style={{ ...ui.notice, marginTop: '14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ display: 'inline-block', width: 14, height: 14, border: `2px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'sxr-spin 0.8s linear infinite' }} />
                Arka planda çalışıyor (binlerce ürün için 1-2 dk sürebilir) — sayfada kalın, sonuç otomatik görünecek.
                <style>{'@keyframes sxr-spin{to{transform:rotate(360deg)}}'}</style>
              </div>
            )}
            {msg && !busy && (
              <div className="sx-break" style={{ ...(msg.type === 'ok' ? ui.notice : s.alertDanger), marginTop: '14px' }}>
                {msg.text}
              </div>
            )}
            {config.last_run_at && (
              <p style={{ ...ui.numMuted, fontSize: '12px', marginTop: '12px' }}>
                Son çalışma: {new Date(config.last_run_at).toLocaleString('tr-TR')} · {config.last_run_count} ürün yazıldı · {config.last_run_outstock} stoksuz dibe · {config.last_run_fail || 0} hata
              </p>
            )}
          </div>

          {preview && (
            <>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {[
                  { l: 'Toplam ürün', v: preview.total },
                  { l: 'Satışı olan', v: preview.withSales },
                  { l: 'Görüntülemesi olan', v: preview.withViews },
                  { l: 'Sepet verisi', v: preview.withCart },
                  { l: 'Stoksuz (dibe)', v: preview.outStock },
                ].map(s => (
                  <div key={s.l} style={{ ...ui.statBox, padding: '14px 18px', minWidth: 120, flex: 1 }}>
                    <div style={{ ...ui.statValue, fontSize: '22px', margin: 0 }}>{s.v ?? '-'}</div>
                    <div style={{ ...ui.statLabel, marginTop: '4px' }}>{s.l}</div>
                  </div>
                ))}
              </div>

              <div style={style.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                  <label style={{ ...style.label, margin: 0 }}>En üst {previewRows.length} (yeni sıra)</label>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={curPage === 0}
                        style={{ ...ui.btnSm, padding: '6px 11px', color: curPage === 0 ? colors.textMuted : colors.textPrimary, cursor: curPage === 0 ? 'default' : 'pointer' }}>◀</button>
                      <select value={curPage} onChange={e => setPage(parseInt(e.target.value))}
                        style={{ ...style.select, padding: '6px 8px' }}>
                        {Array.from({ length: totalPages }, (_, i) => (
                          <option key={i} value={i}>Sayfa {i + 1} / {totalPages}</option>
                        ))}
                      </select>
                      <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={curPage >= totalPages - 1}
                        style={{ ...ui.btnSm, padding: '6px 11px', color: curPage >= totalPages - 1 ? colors.textMuted : colors.textPrimary, cursor: curPage >= totalPages - 1 ? 'default' : 'pointer' }}>▶</button>
                    </div>
                    <button onClick={downloadCsv} style={{ ...ui.btnSm, padding: '7px 14px' }}>⬳ CSV indir</button>
                  </div>
                </div>
                <div className="sx-scroll-x" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={style.th}>#</th><th style={style.th}>Ürün Kodu</th><th style={style.th}>Ürün</th><th style={style.th}>Yayınevi</th><th style={style.th}>Yazar</th><th style={style.th}>Skor</th>
                        <th style={style.th}>Satış</th><th style={style.th}>Görüntüleme</th><th style={style.th}>Stok</th><th style={style.th}>İnd%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((p, i) => (
                        <tr key={p.code}>
                          <td style={{ ...style.td, ...ui.numMuted }}>{curPage * PER + i + 1}</td>
                          <td style={{ ...style.td, color: colors.textSecondary }}>{p.code}</td>
                          <td style={style.td}>{p.name}</td>
                          <td style={{ ...style.td, color: colors.textSecondary }}>{p.publisher}</td>
                          <td style={{ ...style.td, color: colors.textSecondary }}>{p.author}</td>
                          <td style={{ ...style.td, ...ui.num }}>{p.score}</td>
                          <td style={{ ...style.td, ...ui.numMuted }}>{p.sales}</td>
                          <td style={{ ...style.td, ...ui.numMuted }}>{p.views}</td>
                          <td style={{ ...style.td, ...ui.numMuted }}>{p.stock}</td>
                          <td style={{ ...style.td, ...ui.numMuted }}>{p.disc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ ...ui.numMuted, fontSize: '12px', marginTop: '10px' }}>
                  {curPage * PER + 1}-{Math.min((curPage + 1) * PER, previewRows.length)} / {previewRows.length} ürün gösteriliyor · CSV tüm {previewRows.length} satırı indirir
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
